import {
  OrganisationQuoteTemplatesFilters,
  OrganisationQuoteTemplatesSorting,
  TemplateSearchFiltersInput,
} from "../../../../clients/graphqlTypes.js";
import { useFiltersAndPagination } from "../../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { useStateWithUrlParam } from "../../../commons/hooks/useStateWithUrlParam.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../../constants.js";
import { DEFAULT_SORTING } from "./TemplatesQuoteFilterModal.js";

export type TemplatesCollectionView = Extract<
  CollectionView,
  "table" | "list" | "gallery"
>;

export const useTemplatesQuoteList = <
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
>({
  activeViewStorageKey,
  activeViewInitialDesktopValue,
  activeViewInitialMobileValue,
  initialFilters,
  initialSorting,
  initialSearchTerm,
  searchUrlParam = "search",
  pageUrlParam = "page",
  perPageUrlParam = "pageSize",
  filtersUrlParam = "filters",
  sortingsUrlParam = "sortings",
}: {
  activeViewStorageKey: string;
  activeViewInitialDesktopValue: TemplatesCollectionView;
  activeViewInitialMobileValue: TemplatesCollectionView;
  initialFilters: Filters;
  initialSorting?: OrganisationQuoteTemplatesSorting[];
  initialSearchTerm?: string;
  searchUrlParam?: string;
  pageUrlParam?: string;
  perPageUrlParam?: string;
  filtersUrlParam?: string;
  sortingsUrlParam?: string;
}) => {
  const {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationQuoteTemplatesSorting, Filters>(
    initialFilters,
    initialSorting ?? DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE,
    pageUrlParam,
    perPageUrlParam,
    filtersUrlParam,
    sortingsUrlParam
  );

  const [searchTerm, setSearchTerm] = useStateWithUrlParam<string>(
    searchUrlParam,
    initialSearchTerm ?? "",
    toRemoveParams
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: initialSorting ?? DEFAULT_SORTING,
  };

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<TemplatesCollectionView>(
      activeViewStorageKey,
      activeViewInitialDesktopValue,
      activeViewInitialMobileValue
    );

  return {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    activeView,
    setActiveView,
    queryBaseVariables: QUERY_BASE_VARIABLES,
    toRemoveParams,
    searchTerm,
    setSearchTerm,
  };
};
